import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Eye, Compass, Blocks, Palette, Sun, ShieldCheck, HandHeart, Leaf } from "lucide-react";
import { IMAGES } from "@/constants/site";
import { Reveal, Chapter, Stagger, StaggerItem } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";

const APPROACHES = [
  { icon: Blocks, name: "Creative Curriculum", desc: "Hands-on, theme-based learning that follows the child's curiosity." },
  { icon: Palette, name: "Montessori", desc: "Self-directed activity with real materials that build independence." },
  { icon: Sun, name: "Reggio Emilia", desc: "The environment as the third teacher — wonder-led discovery." },
];

const VALUES = [
  { icon: HeartHandshake, title: "Warmth First", desc: "Every child is greeted by name, every single day." },
  { icon: ShieldCheck, title: "Safety Always", desc: "Secure campus, verified staff, gentle supervision." },
  { icon: HandHeart, title: "Sanskar & Values", desc: "Respect, honesty and kindness practised, not just taught." },
  { icon: Leaf, title: "Learn by Doing", desc: "Muddy hands and big questions are welcome here." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pb-20 md:pb-0 pt-36 md:pt-44">
      <header className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-4">About Us</p>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tight leading-[1.02] max-w-3xl">
            Where little blooms grow <span className="text-brand-deepcoral">fearless</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-slate-600">
            Five chapters of who we are, what we believe, and how your child learns with us.
          </p>
        </Reveal>
      </header>

      <div className="mt-16"><EditorialMarquee /></div>

      {/* 01 WHO WE ARE */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal><Chapter number="01" kicker="Who We Are" title="A neighbourhood school with a big heart" /></Reveal>
        <div className="mt-12 grid items-center gap-12 lg:grid-cols-2">
          <Reveal delay={0.1}>
            <div className="blob-a overflow-hidden border-8 border-white shadow-[0_25px_60px_rgba(2,132,199,0.18)]">
              <img src={IMAGES.circle} alt="Children holding hands in a circle" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.2} className="space-y-5 text-slate-600 leading-relaxed">
            <p>
              Founded in <strong className="text-ink">2012 in Murlipura, Jaipur</strong>, Blooming Chaitanya Academy began with one
              simple belief — that a child's first school should feel like a second home.
            </p>
            <p>
              Over 14+ years, we have grown from a small preschool into a Govt. certified school following the
              <strong className="text-ink"> RBSE based pattern</strong>, guiding children from their first steps at 18 months
              all the way to Class 8.
            </p>
            <p>
              Our ethos is simple: keep classes small, keep teachers loving, keep learning joyful — and every child blooms.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 02 VISION & MISSION */}
      <section className="bg-white/60 py-24 md:py-32 grain relative">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal><Chapter number="02" kicker="Vision & Mission" title="What we wake up for" /></Reveal>
          <Stagger className="mt-12 grid gap-8 md:grid-cols-2">
            <StaggerItem>
              <div data-testid="vision-card" className="h-full rounded-[2.5rem] bg-brand-navy p-10 text-white shadow-xl">
                <Eye className="h-10 w-10 text-brand-sun" />
                <h3 className="mt-5 font-display text-2xl font-semibold">Our Vision</h3>
                <p className="mt-3 text-sky-200/85 leading-relaxed">
                  To nurture confident, kind and curious young Indians who love learning — children who walk into any
                  classroom, and any life, with their heads high.
                </p>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div data-testid="mission-card" className="h-full rounded-[2.5rem] bg-brand-sun p-10 text-ink shadow-xl">
                <Compass className="h-10 w-10 text-brand-deepcoral" />
                <h3 className="mt-5 font-display text-2xl font-semibold">Our Mission</h3>
                <p className="mt-3 text-ink/75 leading-relaxed">
                  To provide play-based, child-centered education on the RBSE pattern with low student-teacher ratios,
                  experienced faculty and fees every family can afford.
                </p>
              </div>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      {/* 03 PHILOSOPHY */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal><Chapter number="03" kicker="Educational Philosophy" title="Play is serious learning" /></Reveal>
        <Reveal delay={0.1} className="mt-8 max-w-2xl">
          <p className="text-slate-600 leading-relaxed">
            Our classrooms blend three world-renowned early-childhood approaches into one warm, play-based,
            child-centered day.
          </p>
        </Reveal>
        <Stagger className="mt-12 grid gap-8 md:grid-cols-3">
          {APPROACHES.map((a) => (
            <StaggerItem key={a.name}>
              <div data-testid={`approach-${a.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="group h-full rounded-[2.5rem] border border-amber-200/60 bg-white p-8 shadow-[0_15px_40px_rgba(2,132,199,0.08)] transition-all duration-300 hover:-translate-y-2">
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-coral/15 text-brand-deepcoral transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                  <a.icon className="h-7 w-7" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold">{a.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* 04 PRINCIPAL'S MESSAGE */}
      <section className="bg-brand-navy py-24 md:py-32 grain relative overflow-hidden">
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-brand-coral/20 blur-3xl" />
        <div className="mx-auto max-w-5xl px-6">
          <Reveal><div className="[&_h2]:text-white [&_span.text-outline]:[-webkit-text-stroke-color:rgba(255,255,255,0.25)]"><Chapter number="04" kicker="Principal's Message" title="A note from Ram Ratan Saini" /></div></Reveal>
          <Reveal delay={0.15}>
            <div data-testid="principal-message" className="mt-12 space-y-6 text-sky-100/90 leading-relaxed text-base md:text-lg">
              <p className="font-hand text-3xl md:text-4xl text-brand-sun">Dear Parents,</p>
              <p>
                Choosing your child's first school is an act of trust, and I do not take that lightly. When we opened our
                doors in 2012, I promised every parent in Murlipura the same thing I would want for my own children —
                a place that is safe, loving and honest about learning.
              </p>
              <p>
                Here, your child will not be rushed. They will play, ask questions, make friends, stumble and get up again.
                Our teachers follow the RBSE pattern, yes — but they follow the child first. Marks will come; what matters
                more is that your child loves coming to school every morning.
              </p>
              <p>
                I invite you to visit us, sit in our classrooms, and watch the children bloom. That sight says more than
                any prospectus ever can.
              </p>
              <div className="flex items-center gap-4 pt-2">
                <img
                  src="/images/principal.jpg"
                  alt="Ram Ratan Saini, Principal"
                  className="h-16 w-16 rounded-full object-cover border-4 border-brand-sun shadow-lg"
                />
                <p className="font-display text-xl font-semibold text-white">
                  Ram Ratan Saini <span className="block text-sm font-body font-semibold text-sky-300">Principal, Blooming Chaitanya Academy</span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 05 VALUES */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal><Chapter number="05" kicker="Values & Approach" title="How we do things here" /></Reveal>
        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <StaggerItem key={v.title}>
              <div data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]/g, "-")}`} className="h-full rounded-3xl bg-brand-sky/10 p-7 transition-transform duration-300 hover:-translate-y-1.5">
                <v.icon className="h-8 w-8 text-brand-deepsky" />
                <h3 className="mt-4 font-display text-lg font-semibold">{v.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{v.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-16 text-center">
          <Link to="/admissions" data-testid="about-cta" className="group inline-flex min-h-[52px] items-center gap-2 rounded-full bg-brand-coral px-8 font-bold text-white shadow-[0_10px_30px_rgba(251,113,133,0.4)] transition-transform duration-300 hover:scale-105">
            Begin Admission Enquiry <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
